import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUser, getUserWallet } from "../../api/apiServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

// ── Skeleton ──────────────────────────────────────────────────
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-slate-200 rounded-xl ${className}`} />
);

export default function RewardsPage() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);

  const referralLink = user
    ? `${window.location.origin}/auth?ref=${user.referralCode}`
    : "";

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [userData, walletData] = await Promise.all([
          getUser(),
          getUserWallet(),
        ]);
        if (userData?.status) setUser(userData.user);
        if (walletData?.status) setWallet(walletData.data);

        // Fetch referral history if your API supports it
        // const refData = await getReferralHistory();
        // if (refData?.status) setReferrals(refData.data || []);

        // Mock referral history until API is ready
        setReferrals([
          { id: 1, name: "J. Adeyemi", joinedAt: "2025-05-01", reward: 500, status: "credited" },
          { id: 2, name: "K. Okonkwo", joinedAt: "2025-04-28", reward: 500, status: "credited" },
          { id: 3, name: "T. Balogun", joinedAt: "2025-04-20", reward: 500, status: "pending" },
        ]);
      } catch (err) {
        console.error("Failed to load rewards:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).then(() => {
      toast.success("Referral link copied!");
    });
  };

  const copyCode = () => {
    navigator.clipboard.writeText(user?.referralCode || "").then(() => {
      toast.success("Referral code copied!");
    });
  };

  const handleWithdraw = async () => {
    if (!wallet?.referralBalance || wallet.referralBalance < 1000) {
      return toast.warn("Minimum withdrawal is ₦1,000");
    }
    setWithdrawing(true);
    try {
      // await withdrawReferralBalance();
      toast.success("Referral balance moved to your wallet!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Withdrawal failed");
    } finally {
      setWithdrawing(false);
    }
  };

  const referralBalance = wallet?.referralBalance || 0;
  const totalEarned = referrals
    .filter(r => r.status === "credited")
    .reduce((sum, r) => sum + r.reward, 0);
  const totalReferrals = referrals.length;
  const pendingReferrals = referrals.filter(r => r.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-24" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* Header */}
      <div className="bg-white px-5 pt-12 pb-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <i className="ri-arrow-left-line text-slate-600 text-lg" />
          </button>
          <div>
            <h1 className="text-slate-800 font-bold text-lg">Rewards & Referrals</h1>
            <p className="text-slate-400 text-xs">Earn by inviting friends</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-5 space-y-5">

        {/* ── Hero balance card ── */}
        {loading ? (
          <div className="rounded-3xl p-6" style={{ background: "linear-gradient(135deg, #0F4C35, #1A6B4A)" }}>
            <div className="h-3 w-32 bg-white/20 rounded-full animate-pulse mb-3" />
            <div className="h-10 w-40 bg-white/20 rounded-xl animate-pulse mb-2" />
            <div className="h-3 w-24 bg-white/15 rounded-full animate-pulse mb-6" />
            <div className="h-12 bg-white/15 rounded-2xl animate-pulse" />
          </div>
        ) : (
          <div className="rounded-3xl p-6 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #0F4C35 0%, #1A6B4A 60%, #0F4C35 100%)" }}>
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full opacity-10 pointer-events-none"
              style={{ background: "radial-gradient(circle, #fff, transparent)", transform: "translate(30%,-30%)" }} />

            <div className="relative z-10">
              <p className="text-emerald-200 text-xs font-medium mb-1">Referral Balance</p>
              <p className="text-white text-4xl font-bold mb-1">
                ₦{referralBalance.toLocaleString("en-NG")}
              </p>
              <p className="text-emerald-300 text-xs mb-5">
                Total earned: <span className="text-white font-semibold">₦{totalEarned.toLocaleString()}</span>
              </p>

              <button
                onClick={handleWithdraw}
                disabled={withdrawing || referralBalance < 1000}
                className="w-full py-3 rounded-2xl text-sm font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: referralBalance >= 1000 ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)", color: "#fff", border: "1px solid rgba(255,255,255,0.2)" }}
              >
                {withdrawing ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Moving to wallet…</>
                ) : (
                  <><i className="ri-arrow-down-circle-line" />
                  {referralBalance >= 1000 ? "Move to Naira Wallet" : `Min ₦1,000 to withdraw`}</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── Stats row ── */}
        {loading ? (
          <div className="grid grid-cols-3 gap-3">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Total Referrals", value: totalReferrals, icon: "ri-team-line", color: "#059669" },
              { label: "Pending", value: pendingReferrals, icon: "ri-time-line", color: "#D97706" },
              { label: "Reward/Friend", value: "₦500", icon: "ri-gift-line", color: "#7C3AED" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl border border-slate-100 p-3 text-center">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2"
                  style={{ background: stat.color + "15" }}>
                  <i className={`${stat.icon} text-base`} style={{ color: stat.color }} />
                </div>
                <p className="text-slate-800 font-bold text-base">{stat.value}</p>
                <p className="text-slate-400 text-[10px] mt-0.5 leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Referral link card ── */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-emerald-100 rounded-xl flex items-center justify-center">
              <i className="ri-links-line text-emerald-600 text-base" />
            </div>
            <p className="text-slate-800 font-bold text-sm">Your Referral Link</p>
          </div>

          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : (
            <div className="space-y-3">
              {/* Referral code */}
              <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                <div>
                  <p className="text-slate-400 text-xs mb-0.5">Referral Code</p>
                  <p className="text-slate-800 font-bold font-mono tracking-widest text-sm">
                    {user?.referralCode || "—"}
                  </p>
                </div>
                <button onClick={copyCode}
                  className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors">
                  <i className="ri-file-copy-line" /> Copy
                </button>
              </div>

              {/* Full link */}
              <div className="bg-slate-50 rounded-xl px-4 py-3">
                <p className="text-slate-400 text-xs mb-1">Referral Link</p>
                <p className="text-slate-600 text-xs font-mono truncate mb-2">{referralLink}</p>
                <div className="flex gap-2">
                  <button onClick={copyLink}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 py-2 rounded-lg hover:bg-emerald-100 transition-colors border border-emerald-200">
                    <i className="ri-file-copy-line" /> Copy Link
                  </button>
                  <button
                    onClick={() => navigator.share?.({ title: `Join ${APP_NAME}`, url: referralLink })
                      .catch(() => copyLink())}
                    className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                    <i className="ri-share-line" /> Share
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── How it works ── */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <p className="text-slate-800 font-bold text-sm mb-4">How it works</p>
          <div className="space-y-4">
            {[
              { step: "1", text: "Share your referral link or code with friends", icon: "ri-share-box-line", color: "#059669" },
              { step: "2", text: "Friend signs up using your link and completes KYC", icon: "ri-user-add-line", color: "#0284C7" },
              { step: "3", text: "You earn ₦500 instantly credited to your referral balance", icon: "ri-money-dollar-circle-line", color: "#7C3AED" },
              { step: "4", text: "Move your referral balance to your Naira wallet anytime", icon: "ri-wallet-3-line", color: "#D97706" },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: item.color + "15" }}>
                  <i className={`${item.icon} text-base`} style={{ color: item.color }} />
                </div>
                <div className="flex-1 pt-1">
                  <p className="text-slate-600 text-sm leading-relaxed">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Referral history ── */}
        <div>
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3">
            Referral History ({referrals.length})
          </p>

          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-10 h-10 rounded-full" />
                    <div className="space-y-1.5">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-2.5 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-3 w-16" />
                </div>
              ))}
            </div>
          ) : referrals.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <i className="ri-team-line text-slate-400 text-2xl" />
              </div>
              <p className="text-slate-600 font-semibold text-sm mb-1">No referrals yet</p>
              <p className="text-slate-400 text-xs">Share your link and start earning ₦500 per friend</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden divide-y divide-slate-50">
              {referrals.map((ref) => (
                <div key={ref.id} className="flex items-center justify-between px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-700 font-bold text-sm">
                        {ref.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-slate-800 text-sm font-semibold">{ref.name}</p>
                      <p className="text-slate-400 text-xs">
                        {new Date(ref.joinedAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-600">+₦{ref.reward.toLocaleString()}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      ref.status === "credited"
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-amber-50 text-amber-600"
                    }`}>
                      {ref.status === "credited" ? "Credited" : "Pending"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MobileBottomNav />
    </div>
  );
}

const APP_NAME = "CryptoNG";
